import { EventManager, Patch } from "@ploy-lib/calculation";
import { MinimalInitialData } from "@ploy-lib/rest-resources";
import { parseSearchParams } from "@ploy-lib/routing";
import { initialSender } from "@ploy-lib/snippet-messaging-types";

export const bootstrapEventProviderForSnippetMessaging = (
	eventProvider: EventManager<string, string | number | boolean>,
	allowedOrigin: string
) => {
	type DefinedPatch = Patch<string, string | number | boolean>;

	const qsParams = parseSearchParams<
		{
			ext?: MinimalInitialData;
		} & Record<string, any>
	>(window.location.search);

	const unsubscribe = eventProvider.calculatorReady.subscribe(() => {
		//console.log("ready");

		unsubscribe();

		const messagingEvents = initialSender(allowedOrigin);

		messagingEvents.data_payload.subscribe(async data => {
			//console.log("received data payload");

			const makeNamespacePatcher =
				(namespace: string) =>
				([name, value]: [string, any]): DefinedPatch => ({
					overwrite: true,
					isResolve: true,
					namespace,
					target: name,
					clickTrigger:
						name === "CustomerNumber" || name === "CustomerNumberPrivate",
					value
				});

			let patches: DefinedPatch[] = [];

			if (data.Main) {
				patches.push(
					...Object.entries(data.Main)
						.filter(([_, value]) => value != null)
						.map(makeNamespacePatcher("Main"))
				);
			}

			if (data.Object) {
				patches.push(
					...Object.entries(data.Object)
						.filter(([_, value]) => value != null)
						.map(makeNamespacePatcher("Object"))
				);
			}

			if (patches.length > 0) {
				eventProvider.patch.notify({ patches });
				patches = [];
			}

			const handledCustomerFields = [
				"CustomerType",
				"CustomerNumber",
				"CustomerNumberPrivate"
			];

			if (data.Customer) {
				// Handle (main) customer by:
				//  1. Set relevant customer type
				//  2. Perform customer lookup and wait until completion
				//  3. Process residual fields

				const makeCustomerPatch = makeNamespacePatcher("Customer");

				const customerData = data.Customer;

				// How to reliably detect this patch has been processed and/or triggered a service?
				// We know this patch triggers the calculator service, but what about requests in flight?
				if (!qsParams.ext?.customerTypeId) {
					// Avoid updating customerType if already set via url, mainly because customerType _can_ be wrong
					eventProvider.patch.notify({
						patches: [
							makeCustomerPatch(["CustomerType", customerData.CustomerType])
						]
					});
				}

				let customerNumberData: [string, any] | null = null;
				if (
					customerData.CustomerType === 1 &&
					customerData.CustomerNumberPrivate != null
				)
					customerNumberData = [
						"CustomerNumberPrivate",
						customerData.CustomerNumberPrivate
					];
				else if (
					(customerData.CustomerType === 2 ||
						customerData.CustomerType === 3) &&
					customerData.CustomerNumber != null &&
					!qsParams.ext?.customerNumber
				) {
					// Avoid updating customernumber if already set via url querystring (parsed in ProductView and passed on to CalculationManager), system should have found customer already on init.
					customerNumberData = ["CustomerNumber", customerData.CustomerNumber];
				}

				if (customerNumberData != null) {
					// TODO: handle "failures" somehow?
					const customerLookupSuccess = serviceSuccessWaiter(eventProvider, [
						{ namespace: "Customer", name: "Wait", weight: 1 },
						{ namespace: "Customer", name: "Update", weight: 2 }
					]);

					// Trigger the customer lookup
					eventProvider.patch.notify({
						patches: [makeCustomerPatch(customerNumberData)]
					});

					await customerLookupSuccess;
				}

				// Processs rest of the fields
				patches.push(
					...Object.entries(customerData)
						.filter(([_, value]) => value != null)
						.filter(([key, _]) => !handledCustomerFields.includes(key))
						.map(makeCustomerPatch)
				);
			}

			if (data.CoSigner) {
				// Handle CoSigner by:
				//  1. Set customer type
				//  2. Perform customer lookup and wait until completion
				//  3. Process residual fields

				const makeCoSignerPatch = makeNamespacePatcher("CoSigner");

				const coSignerData = data.CoSigner;

				eventProvider.patch.notify({
					patches: [
						makeCoSignerPatch(["CustomerType", coSignerData.CustomerType])
					]
				});

				let customerNumberData: [string, any] | null = null;

				if (
					coSignerData.CustomerType === 1 &&
					coSignerData.CustomerNumberPrivate != null
				) {
					customerNumberData = [
						"CustomerNumberPrivate",
						coSignerData.CustomerNumberPrivate
					];
				} else if (
					(coSignerData.CustomerType === 2 ||
						coSignerData.CustomerType === 3) &&
					coSignerData.CustomerNumber != null
				) {
					customerNumberData = ["CustomerNumber", coSignerData.CustomerNumber];
				}

				if (customerNumberData != null) {
					const coSignerLookupSuccess = serviceSuccessWaiter(eventProvider, [
						{ namespace: "CoSigner", name: "Wait", weight: 1 },
						// Note that Update and GetConditions can happen in
						// any order. Our only criteria is they have to
						// happen after Wait.
						{ namespace: "CoSigner", name: "Update", weight: 2 },
						{ namespace: "Calculator", name: "Conditions", weight: 2 }
					]);

					// Trigger CoSigner lookup
					eventProvider.patch.notify({
						patches: [makeCoSignerPatch(customerNumberData)]
					});

					await coSignerLookupSuccess;
				}

				// Processs rest of the fields
				patches.push(
					...Object.entries(coSignerData)
						.filter(([_, value]) => value != null)
						.filter(([key, _]) => !handledCustomerFields.includes(key))
						.map(makeCoSignerPatch)
				);
			}

			if (data.Supplier) {
				patches.push(
					...Object.entries(data.Supplier)
						.filter(([_, value]) => value != null)
						.map(makeNamespacePatcher("Supplier"))
				);
			}

			if (data.Calculator) {
				if (patches.length > 0) {
					eventProvider.patch.notify({ patches });
					patches = [];
				}
				//Patch ObjectPrice First
				const handledPriceFields = [
					"ObjectPrice",
					"ObjectPriceIncVat",
					"ObjectPriceExVat"
				];
				patches.push(
					...Object.entries(data.Calculator)
						.filter(([_, value]) => value != null)
						.filter(([name, _]) => handledPriceFields.includes(name))
						.map(makeNamespacePatcher("Calculator"))
				);

				if (patches.length > 0) {
					eventProvider.patch.notify({ patches });
					patches = [];

					//Wait for GetConditions Success after ObjectPrice change before we send more Calculator data
					await serviceSuccessWaiter(eventProvider, [
						{ namespace: "Calculator", name: "Conditions", weight: 1 }
					]);
				}

				patches.push(
					...Object.entries(data.Calculator)
						.filter(([_, value]) => value != null)
						.map(makeNamespacePatcher("Calculator"))
				);
			}

			//console.log("prepared patches", patches);

			if (patches.length > 0) {
				eventProvider.patch.notify({ patches });
			}
		});

		messagingEvents.trigger_change.subscribe(async data => {
			let patch: DefinedPatch = {
				namespace: data.namespace,
				target: data.field,
				value: data.value,
				overwrite: true,
				isResolve: true,
				changeTrigger: data.field
			};

			eventProvider.patch.notify({patches : [patch]});
		});
	});
};

const serviceSuccessWaiter = <TN extends string, TD>(
	eventProvider: EventManager<TN, TD>,
	orderedServicesToWaitFor: Readonly<
		Readonly<{ namespace: string; name: string; weight: number }>[]
	>
) =>
	new Promise<void>((resolve, reject) => {
		let svcQueue = [...orderedServicesToWaitFor];

		const unsubscribe = eventProvider.serviceSuccess.subscribe(
			({ namespace, service }) => {
				if (
					svcQueue.length > 0 &&
					svcQueue.some(
						s =>
							s.name === service.name &&
							s.namespace === namespace &&
							s.weight === svcQueue[0].weight
					)
				) {
					svcQueue.splice(
						svcQueue.findIndex(
							s =>
								s.name === service.name &&
								s.namespace === namespace &&
								s.weight === svcQueue[0].weight
						),
						1
					);
				}

				if (svcQueue.length === 0) {
					unsubscribe();
					resolve();
				}
			}
		);
	});
